import {
  MergeRecoveryControls,
  type RecoveryPreview,
} from "./MergeRecoveryControls";
import { useEffect, useRef, useState } from "react";
import { apiFetch } from "../../stores/authStore";

type Row = {
  orderId: string;
  tokenId: string;
  retainedAtomic: string;
  venueRemainingAtomic: string | null;
  reconciliation: string;
};
export type ReservationPreview = {
  orgId: string;
  credentialId: string;
  wallet: string;
  observedAtMs: number;
  completedAtMs: number;
  admissionConfigured: boolean;
  rows: Row[];
  totals: { tokenId: string; retainedAtomic: string }[] | null;
  cash?: {
    admissionConfigured: boolean;
    retainedCashAtomic: string | null;
    reasons: string[];
    rows: {
      orderId: string;
      tokenId: string;
      retainedCashAtomic: string | null;
      state: string;
      observedOpen: boolean;
    }[];
  };
  recovery?: RecoveryPreview;
  observations: { orderId: string; state: string }[];
  reasons: string[];
};
const reasons: Record<string, string> = {
  external_and_other_key_activity_unexcluded:
    "Other keys, deployments and external wallet activity are not covered.",
  settlement_not_reconciled:
    "Matched fills have not been reconciled to settled token balances.",
  wallet_exclusion_not_implemented:
    "Wallet-wide trading exclusion has not been verified across all writers.",
  open_orders_unavailable:
    "The selected key’s open orders could not be read completely.",
  exact_read_budget_exceeded:
    "Only the first eight eligible receipts received an individual order check.",
};
function quantity(raw: string | null) {
  if (raw === null) return "Unavailable";
  const n = BigInt(raw),
    whole = n / 1_000_000n,
    fraction = (n % 1_000_000n).toString().padStart(6, "0").replace(/0+$/, "");
  return whole.toString() + (fraction ? `.${fraction}` : "");
}
/** Manual, admin-only diagnostic. Opening/refreshing never opts a wallet in. */
export function PolyMergePreview({
  orgId,
  credentials,
}: {
  orgId: string;
  credentials: { id: string; label: string }[];
}) {
  const [credentialId, setCredentialId] = useState("");
  const [result, setResult] = useState<ReservationPreview | null>(null);
  const [recovering, setRecovering] = useState(false);
  const [recoveryNotice, setRecoveryNotice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const request = useRef<AbortController | null>(null);
  const selected = credentials.some((c) => c.id === credentialId);
  const account = `${orgId}:${credentialId}:${selected}`;
  const [renderedAccount, setRenderedAccount] = useState(account);
  // Clear evidence before committing a different account's UI.
  if (renderedAccount !== account) {
    setRenderedAccount(account);
    setResult(null);
    setError(null);
    setRecoveryNotice(null);
    setRecovering(false);
    setPending(false);
  }
  useEffect(
    () => () => {
      request.current?.abort();
    },
    [account],
  );
  async function read() {
    request.current?.abort();
    const controller = new AbortController();
    request.current = controller;
    setPending(true);
    setResult(null);
    setError(null);
    try {
      const res = await apiFetch(
        `/api/admin/poly-merge-preview?${new URLSearchParams({ orgId, credentialId })}`,
        { signal: controller.signal },
      );
      if (!res.ok)
        throw Error(
          "Reservation evidence is unavailable. Try again; auto-merge remains off.",
        );
      const data = (await res.json()) as ReservationPreview;
      if (data.orgId !== orgId || data.credentialId !== credentialId)
        throw Error("Account changed. Read the preview again.");
      if (!controller.signal.aborted) setResult(data);
    } catch (e) {
      if (!controller.signal.aborted)
        setError(e instanceof Error ? e.message : "Preview unavailable.");
    } finally {
      if (!controller.signal.aborted) setPending(false);
    }
  }
  return (
    <section
      aria-label="Auto-merge preview"
      className="border-border mt-4 min-w-0 border-t pt-4 text-xs"
    >
      <div className="flex flex-wrap items-center gap-3">
        <h4 className="font-semibold">Auto-merge</h4>
        <span className="bg-muted rounded px-2 py-1 font-medium">
          {result?.recovery?.mergeEnabled || result?.recovery?.splitEnabled
            ? "Execution off · stored opt-in on"
            : "Off · opt-in required"}
        </span>
        <span className="text-muted-foreground">
          Activation is not available in this release.
        </span>
      </div>
      <p className="text-muted-foreground my-2">
        Read reservation evidence for one key. This does not enable merging,
        release inventory or submit transactions.
      </p>
      <div className="flex flex-wrap items-center gap-2">
        <label htmlFor="merge-preview-key">Key</label>
        <select
          id="merge-preview-key"
          className="bg-background border-border max-w-full rounded border p-2"
          value={selected ? credentialId : ""}
          onChange={(e) => setCredentialId(e.target.value)}
        >
          <option value="">Select a key</option>
          {credentials.map((c) => (
            <option key={c.id} value={c.id}>
              {c.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="border-border rounded border px-3 py-2 disabled:opacity-50"
          disabled={!selected || pending || recovering}
          onClick={() => void read()}
        >
          {pending ? "Reading…" : "Read reservation preview"}
        </button>
      </div>
      {error && (
        <p role="alert" className="mt-2 text-red-500">
          {error}
        </p>
      )}
      {recoveryNotice && (
        <p role="status" className="mt-2">
          {recoveryNotice}
        </p>
      )}
      {result && <ReservationEvidence result={result} />}
      {result?.recovery && (
        <MergeRecoveryControls
          key={account}
          target={{ orgId, credentialId, wallet: result.wallet }}
          preview={result.recovery}
          onPending={() => setRecovering(true)}
          onFinished={(notice) => {
            setRecovering(false);
            setRecoveryNotice(notice);
            void read();
          }}
        />
      )}
    </section>
  );
}
export function ReservationEvidence({
  result,
}: {
  result: ReservationPreview;
}) {
  const observations = new Map(
    result.observations.map((o) => [o.orderId, o.state]),
  );
  return (
    <div className="mt-3 min-w-0 space-y-2" role="status">
      <p className="font-medium">
        Merge quantity unavailable · incomplete coverage
      </p>
      <p className="break-all">Wallet: {result.wallet}</p>
      <p className="text-muted-foreground">
        Historical observation: {new Date(result.observedAtMs).toISOString()} —{" "}
        {new Date(result.completedAtMs).toISOString()}. Read again for a new
        observation.
      </p>
      <p>
        SELL receipt capture:{" "}
        {result.admissionConfigured
          ? "configured for this wallet"
          : "off for this wallet"}
        . This is separate from auto-merge.
      </p>
      {result.cash && (
        <section
          aria-label="BUY cash reservations"
          className="border-border space-y-2 border-y py-3"
        >
          <h5 className="font-medium">BUY cash commitments</h5>
          <p>
            Receipt capture:{" "}
            {result.cash.admissionConfigured
              ? "configured for this wallet"
              : "off for this wallet"}
            .
          </p>
          <p>
            Retained signed principal:{" "}
            <span className="tabular-nums">
              {result.cash.retainedCashAtomic === null
                ? "Unavailable"
                : `${quantity(result.cash.retainedCashAtomic)} pUSD`}
            </span>
          </p>
          <p className="text-muted-foreground">
            Fees and other wallet activity are not covered. This is not an
            available cash balance. Missing or filled orders retain their signed
            principal until settlement is verified.
          </p>
          {!!result.cash.reasons.length && (
            <p className="text-amber-600 dark:text-amber-400">
              Total withheld:{" "}
              {result.cash.reasons.map((r) => r.replace(/_/g, " ")).join("; ")}.
            </p>
          )}
          {!!result.cash.rows.length && (
            <div className="max-h-64 overflow-auto">
              <table className="w-full min-w-[650px] text-left">
                <thead>
                  <tr>
                    {["Order / token", "Retained principal", "Observation"].map(
                      (h) => (
                        <th key={h} className="p-2 font-medium">
                          {h}
                        </th>
                      ),
                    )}
                  </tr>
                </thead>
                <tbody>
                  {result.cash.rows.map((r) => (
                    <tr
                      key={r.orderId}
                      className="border-border border-t align-top"
                    >
                      <td className="max-w-64 break-all p-2">
                        {r.orderId}
                        <div className="text-muted-foreground">
                          Token {r.tokenId}
                        </div>
                      </td>
                      <td className="p-2 tabular-nums">
                        {quantity(r.retainedCashAtomic)}
                      </td>
                      <td className="p-2">
                        {r.state.replace(/_/g, " ")} ·{" "}
                        {r.observedOpen ? "open" : "not observed"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}
      <ul className="list-disc space-y-1 pl-4">
        {result.reasons.map((reason) => (
          <li key={reason}>
            {reasons[reason] ??
              `Evidence conflict: ${reason.replace(/_/g, " ")}.`}
          </li>
        ))}
      </ul>
      <p className="text-muted-foreground">
        Full original SELL quantities remain retained, including matched or
        terminal receipts. An absent order never releases inventory here.
      </p>
      {!result.rows.length ? (
        <p>
          No receipts or visible SELL orders were found. This does not establish
          free inventory.
        </p>
      ) : (
        <div className="max-h-80 overflow-auto rounded border border-border">
          <table className="w-full min-w-[650px] text-left">
            <thead className="bg-muted sticky top-0">
              <tr>
                {[
                  "Order / token",
                  "Retained contracts",
                  "Venue remaining",
                  "Observation",
                ].map((h) => (
                  <th key={h} className="p-2 font-medium">
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {result.rows.map((r) => (
                <tr
                  key={r.orderId}
                  className="border-border border-t align-top"
                >
                  <td className="max-w-64 break-all p-2">
                    <div>{r.orderId}</div>
                    <div className="text-muted-foreground">
                      Token {r.tokenId}
                    </div>
                  </td>
                  <td className="p-2 tabular-nums">
                    {quantity(r.retainedAtomic)}
                  </td>
                  <td className="p-2 tabular-nums">
                    {quantity(r.venueRemainingAtomic)}
                  </td>
                  <td className="p-2">
                    {(observations.get(r.orderId) ?? r.reconciliation) ===
                    "not_observed_retained"
                      ? "Not observed · retained"
                      : (
                          observations.get(r.orderId) ?? r.reconciliation
                        ).replace(/_/g, " ")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {result.totals === null && (
        <p className="text-amber-600 dark:text-amber-400">
          Totals withheld because evidence is incomplete or conflicting.
        </p>
      )}
    </div>
  );
}
